/**
 * Component Name: Youtube Embed
 * This module shows the iframe for a Youtube video embed
 */
import clsx from 'clsx';
import {
  pushVideoCompleteEvent,
  pushVideoErrorEvent,
  pushVideoPauseEvent,
  pushVideoStartEvent,
  pushVideoBufferingEvent,
  pushVideoReplayEvent,
  type VideoAnalyticsPayload,
} from 'components/helpers/Analytics/EmbeddedVideo/EmbeddedVideoAnalytics';
import { useEffect, useRef, useState } from 'react';

const YOUTUBE_IFRAME_SANDBOX = 'allow-scripts allow-presentation';
const YOUTUBE_IFRAME_ALLOW =
  'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
const YOUTUBE_IFRAME_REFERRER_POLICY = 'strict-origin-when-cross-origin';

const applyYoutubeIframeSecurityAttributes = (iframe: HTMLIFrameElement | null) => {
  if (!iframe) return;

  iframe.setAttribute('sandbox', YOUTUBE_IFRAME_SANDBOX);
  iframe.setAttribute('allow', YOUTUBE_IFRAME_ALLOW);
  iframe.setAttribute('referrerpolicy', YOUTUBE_IFRAME_REFERRER_POLICY);
  iframe.setAttribute('allowfullscreen', 'true');
};

// Explicitly declare the YT type in the global scope
declare global {
  interface Window {
    YT: typeof YT; // Declare the YT namespace
    onYouTubeIframeAPIReady: () => void;
  }
}

export interface IYoutubeEmbedProps {
  videoId: string;
  videoTitle: string;
  videoUrl: string;
  autoplay: boolean;
  isPortrait: boolean;
  onVideoComplete?: () => void;
  onVideoPause?: () => void;
  onVideoError?: (error: Error) => void;
  onVideoBuffering?: () => void;
  onVideoReplay?: () => void;
}

const YoutubeEmbed: React.FC<IYoutubeEmbedProps> = ({
  videoId,
  videoTitle,
  autoplay,
  isPortrait,
  videoUrl,
  onVideoComplete,
  onVideoPause,
  onVideoError,
  onVideoBuffering,
  onVideoReplay,
}) => {
  // Focus management
  const containerRef = useRef<HTMLDivElement>(null);
  const [apiReady, setApiReady] = useState(false);
  const playerRef = useRef<YT.Player | null>(null);
  const hasEndedRef = useRef(false);
  const isVisibleRef = useRef(false);

  useEffect(() => {
    if (autoplay && containerRef.current) {
      // Only set focus if the video is lazy loaded
      containerRef.current.focus();
    }
  });

  // Track video visibility
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        isVisibleRef.current = entry.isIntersecting;
      },
      { threshold: 0.5 } // Consider visible when 50% is in viewport
    );

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    // Ensure onYouTubeIframeAPIReady is defined before adding the script
    if (typeof window !== 'undefined' && !window.onYouTubeIframeAPIReady) {
      window.onYouTubeIframeAPIReady = () => setApiReady(true);
    }

    if (typeof window !== 'undefined' && !window.YT) {
      const existingScript = document.querySelector(
        'script[src="https://www.youtube.com/iframe_api"]'
      );
      if (!existingScript) {
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        tag.async = true;
        tag.onload = () => {
          if (window.YT) {
            setApiReady(true);
          }
        };
        const firstScriptTag = document.getElementsByTagName('script')[0];
        firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
      } else if (typeof window !== 'undefined' && window.YT) {
        setApiReady(true);
      }
    } else {
      setApiReady(true);
    }
  }, []);

  useEffect(() => {
    if (!apiReady || !videoId) return;

    const iframeElement = document.getElementById(`youtube-player-${videoId}`);
    if (!iframeElement) return;

    // Helper function to build analytics payload with current video metrics
    const getAnalyticsPayload = (): VideoAnalyticsPayload => {
      const player = playerRef.current;
      let currentTime = 0;
      let duration = 0;
      let percent = 0;

      if (
        player &&
        typeof player.getCurrentTime === 'function' &&
        typeof player.getDuration === 'function'
      ) {
        try {
          currentTime = player.getCurrentTime() || 0;
          duration = player.getDuration() || 0;
          percent = duration > 0 ? Math.round((currentTime / duration) * 100) : 0;
        } catch (error) {
          console.debug('Error getting video metrics:', error);
        }
      }

      return {
        videoId,
        videoTitle,
        videoUrl,
        videoCurrentTime: currentTime,
        videoDuration: duration,
        videoPercent: percent,
        videoVisible: isVisibleRef.current,
        videoProvider: 'youtube',
      };
    };

    let retryCount = 0;
    const maxRetries = 10;

    const initializePlayer = () => {
      if (typeof window !== 'undefined' && window.YT && window.YT.Player) {
        const player = new window.YT.Player(iframeElement, {
          videoId: videoId,
          width: isPortrait ? '360' : '640',
          height: isPortrait ? '640' : '360',
          playerVars: {
            autoplay: autoplay ? 1 : 0,
          },
          events: {
            onReady: (event) => {
              applyYoutubeIframeSecurityAttributes(
                typeof event?.target?.getIframe === 'function' ? event.target.getIframe() : null
              );
            },
            onStateChange: (event) => {
              switch (event.data) {
                case window.YT.PlayerState.ENDED:
                  hasEndedRef.current = true;
                  pushVideoCompleteEvent(getAnalyticsPayload());
                  if (onVideoComplete) onVideoComplete();
                  break;
                case window.YT.PlayerState.PAUSED:
                  pushVideoPauseEvent(getAnalyticsPayload());
                  if (onVideoPause) onVideoPause();
                  break;
                case window.YT.PlayerState.PLAYING:
                  if (hasEndedRef.current) {
                    // Video is replaying after it ended
                    pushVideoReplayEvent(getAnalyticsPayload());
                    if (onVideoReplay) onVideoReplay();
                    hasEndedRef.current = false;
                  } else {
                    // Normal video start
                    pushVideoStartEvent(getAnalyticsPayload());
                  }
                  break;
                case window.YT.PlayerState.BUFFERING:
                  pushVideoBufferingEvent(getAnalyticsPayload());
                  if (onVideoBuffering) onVideoBuffering();
                  break;
              }
            },
            onError: (event) => {
              const errorMessage = `YouTube Player Error: ${event.data}`;
              pushVideoErrorEvent(getAnalyticsPayload(), errorMessage);
              if (onVideoError) onVideoError(new Error(errorMessage));
            },
          },
        });

        playerRef.current = player;
      } else if (retryCount < maxRetries) {
        retryCount++;
        setTimeout(initializePlayer, 100);
      }
    };

    initializePlayer();

    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, [
    apiReady,
    videoId,
    videoTitle,
    videoUrl,
    autoplay,
    isPortrait,
    onVideoComplete,
    onVideoPause,
    onVideoError,
    onVideoBuffering,
    onVideoReplay,
  ]);

  return (
    <div
      className={clsx('iframeEmbedContainer', isPortrait && 'iframeEmbedContainerPortrait')}
      role="region"
      aria-label={videoTitle}
      ref={containerRef}
      tabIndex={-1}
    >
      {typeof window !== 'undefined' && (
        <div id={`youtube-player-${videoId}`} title={videoTitle}></div>
      )}
    </div>
  );
};

export default YoutubeEmbed;
